import {
  booleanAttribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import {
  FormControlDirective,
  FormControlName,
  FormsModule,
  NgModel,
  ReactiveFormsModule,
} from '@angular/forms';
import { NgClass } from '@angular/common';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { injectNgControl } from '@rp/utils';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NoopValueAccessorDirective } from '@rp/shared/directives';
import { HttpImageUploaderProvider, IMAGEUPLOADER_PROVIDER_TOKEN } from '@rp/shared/providers';
import { filter, finalize } from 'rxjs';
import { IMAGE_MAX_RESOLUTION, IMAGE_MIN_RESOLUTION, IMAGE_SIZE } from '@rp/shared/models';

import { IconComponent, IconName, IconSize } from '../icon';
import { ToastService } from '../toaster';
import { SpinnerDirective } from '../spinner';

export type UploadedImageSize = 's' | 'm' | 'l';

@Component({
  selector: 'rp-image-uploader',
  standalone: true,
  templateUrl: './image-uploader.component.html',
  styleUrls: ['./image-uploader.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [NoopValueAccessorDirective],
  imports: [
    IconComponent,
    NgClass,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    SpinnerDirective,
  ],
  providers: [
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { floatLabel: 'always' } },
    { provide: IMAGEUPLOADER_PROVIDER_TOKEN, useClass: HttpImageUploaderProvider },
  ],
})
export class ImageUploaderComponent implements OnInit {
  ngControl: FormControlDirective | FormControlName | NgModel = injectNgControl();

  size = input<UploadedImageSize>('m');
  removeIconSize = input<IconSize>('s');
  title = input<string>('general.imageUpload');
  description = input<string>('general.dragAndDrop');
  fileParamsText = input<string>('general.fileParams');
  fileParams = input<Record<string, string>>({
    height: `${IMAGE_MIN_RESOLUTION}`,
    width: `${IMAGE_MIN_RESOLUTION}`,
    size: `${IMAGE_SIZE}`,
  });
  fullWidth = input(false, {
    transform: booleanAttribute,
  });
  lazyLoad = input(false, {
    transform: booleanAttribute,
  });
  errors = signal<string | null>(null);
  imageSrc = signal<string | ArrayBuffer | null>(null);
  isLoading = signal<boolean>(false);

  imageLoad = output<string>();
  imageLoadError = output<void>();

  isDragging = false;
  isDisabled = false;

  protected readonly icons = IconName;
  private readonly _cdRef = inject(ChangeDetectorRef);
  private readonly _toast = inject(ToastService);
  private readonly _translateService = inject(TranslateService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _imageUploaderProvider = inject(IMAGEUPLOADER_PROVIDER_TOKEN);

  constructor() {
    effect(() => {
      if (this.imageSrc()) {
        this.ngControl.control.setValue(this.imageSrc());
        this._cdRef.markForCheck();
      }
    });
  }

  ngOnInit(): void {
    if (this.ngControl.value) {
      if (this.lazyLoad()) {
        this._lazyLoadImage();
      } else {
        this.imageSrc.set(this.ngControl.value);
      }
    }

    this.ngControl.control.valueChanges
      .pipe(
        filter(value => value === null),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe(() => this.imageSrc.set(null));

    this.ngControl.control.statusChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => this._cdRef.detectChanges());
  }

  onFileSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files[0];
    if (file) {
      this.handleFile(file);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = true;
  }

  onDragLeave(): void {
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    const file = event.dataTransfer?.files[0];
    if (file) {
      this.handleFile(file);
    }
  }

  handleFile(file: File): void {
    if (file?.size > IMAGE_SIZE * 1024 * 1024 || !file.type.startsWith('image/')) {
      this._uploadErrorHandler();

      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const image = new Image();
      image.src = reader.result as string;

      image.onload = () => {
        const width = image.width;
        const height = image.height;

        if (
          width >= IMAGE_MIN_RESOLUTION &&
          height >= IMAGE_MIN_RESOLUTION &&
          width <= IMAGE_MAX_RESOLUTION &&
          height <= IMAGE_MAX_RESOLUTION
        ) {
          this.imageSrc.set(reader.result);
          this.ngControl.control.markAsTouched();
          this.ngControl.control.markAsDirty();
        } else {
          this._uploadErrorHandler();
        }
      };
    };

    reader.readAsDataURL(file);
  }

  remove(): void {
    this.ngControl.control.reset();
    this.imageSrc.set(null);
    this.ngControl.control.markAsTouched();
  }

  private _uploadErrorHandler(): void {
    this.ngControl.control.markAsDirty();
    this._toast.showToast({
      title: this._translateService.instant('general.uploadFailed'),
      type: 'error',
      text: this._translateService.instant('general.uploadFailedMessage', {
        maxResolution: `${IMAGE_MIN_RESOLUTION}x${IMAGE_MIN_RESOLUTION}`,
        minResolution: `${IMAGE_MIN_RESOLUTION}x${IMAGE_MIN_RESOLUTION}`,
        size: IMAGE_SIZE,
      }),
    });
  }

  private _lazyLoadImage(): void {
    this.isLoading.set(true);

    this._imageUploaderProvider
      .getImageFromS3(this.ngControl.value)
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe({
        next: base64 => {
          this.imageSrc.set(base64);

          this.imageLoad.emit(base64);
        },
        error: () => {
          this.imageSrc.set(null);

          this.imageLoadError.emit();
        },
      });
  }
}
